import api from '@/lib/api';

export interface AITrainingData {
  id: string;
  organization_id: string;
  title: string;
  description?: string;
  training_prompt: string;
  category: string;
  example_input?: string;
  example_output?: string;
  is_active: boolean;
  priority: number;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  created_by_last_name?: string;
  updated_by_name?: string;
  updated_by_last_name?: string;
}

export interface AIResponseRevision {
  id: string;
  organization_id: string;
  ticket_id?: string;
  original_response: string;
  revision_prompt: string;
  revised_response?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  requested_by?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  requested_by_name?: string;
  requested_by_last_name?: string;
  ticket_subject?: string;
}

export interface CreateTrainingDataRequest {
  title: string;
  description?: string;
  training_prompt: string;
  category?: string;
  example_input?: string;
  example_output?: string;
  priority?: number;
  organizationId?: string;
}

export interface UpdateTrainingDataRequest {
  title?: string;
  description?: string;
  training_prompt?: string;
  category?: string;
  example_input?: string;
  example_output?: string;
  priority?: number;
  is_active?: boolean;
}

export interface CreateRevisionRequest {
  ticket_id?: string;
  original_response: string;
  revision_prompt: string;
}

export interface TrainingDataResponse {
  success: boolean;
  data: {
    training_data: AITrainingData[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface RevisionResponse {
  success: boolean;
  data: {
    revisions: AIResponseRevision[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

class AITrainingService {
  // Training Data endpoints
  async getTrainingData(params?: {
    page?: number;
    limit?: number;
    category?: string;
    is_active?: string;
    organizationId?: string;
  }): Promise<TrainingDataResponse> {
    const response = await api.get('/ai/training-data', { params });
    return response.data;
  }

  async createTrainingData(data: CreateTrainingDataRequest): Promise<{ success: boolean; data: AITrainingData }> {
    const response = await api.post('/ai/training-data', data);
    return response.data;
  }

  async updateTrainingData(id: string, data: UpdateTrainingDataRequest): Promise<{ success: boolean; data: AITrainingData }> {
    const response = await api.put(`/ai/training-data/${id}`, data);
    return response.data;
  }

  async deleteTrainingData(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/ai/training-data/${id}`);
    return response.data;
  }

  // Response Revision endpoints
  async createRevision(data: CreateRevisionRequest): Promise<{ success: boolean; data: AIResponseRevision }> {
    const response = await api.post('/ai/response-revision', data);
    return response.data;
  }

  async getRevisions(params?: {
    page?: number;
    limit?: number;
    ticket_id?: string;
    status?: string;
  }): Promise<RevisionResponse> {
    const response = await api.get('/ai/response-revisions', { params });
    return response.data;
  }

  // Categories helper
  getCategories(): Array<{ value: string; label: string }> {
    return [
      { value: 'general', label: 'Allgemein' },
      { value: 'tone', label: 'Ton & Stil' },
      { value: 'apologizing', label: 'Entschuldigung' },
      { value: 'solutions', label: 'Lösungen' },
      { value: 'greeting', label: 'Begrüßung' },
      { value: 'closing', label: 'Abschluss' },
      { value: 'escalation', label: 'Eskalation' },
      { value: 'technical', label: 'Technisch' }
    ];
  }
}

export default new AITrainingService();